<template>
  <div class="c-integration-profile-settings-drawer">
    <BittsModal v-if="confirmationModalType !== null" v-bind="modalProps" />
    <BittsDrawer
      visible
      v-if="enabledTray"
      destroy-on-close
      :title="enabledTray.name"
      primary-button-text="Save changes"
      :is-saving="isSavingAll"
      :has-heading-border="false"
      width="610"
      :show-footer="false"
      @action:primary="saveChanges"
      @closed="onDrawerClosed"
    >
      <template #headerAvatar>
        <BittsAvatar
          :org="avatarLogo"
          :is-entity="true"
          :show-initials="true"
          size="medium"
          shape="square"
        />
      </template>

      <template #content>
        <BittsTabs
          v-if="isHubspot || isSalesforce"
          :tabs="tabs"
          :current-value="currentTab"
          @change:tab="onChangeTab"
        />
        <IntegrationSettingsContent
          :active-tab="currentTab"
          @remove-integration="confirmationModalType = 'delete'"
        />
      </template>
      <template #secondaryButton>
        <BittsButton
          text="Remove integration"
          :left-icon="['fak', 'delete']"
          variant="ghost"
          class="w-1/2"
          type="danger"
          data-testid="delete-integration-button"
          @click="confirmationModalType = 'delete'"
          :disabled="isSavingAll || !hasPermissionToManageIntegrations"
        />
      </template>
    </BittsDrawer>
  </div>
</template>
<script setup lang="ts">
import {
  BittsAvatar,
  BittsButton,
  BittsDrawer,
  BittsModal,
  BittsTabs,
} from '@crossbeam/bitts';
import { BittsIconPrefix } from '@crossbeam/bitts/src/types';

import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import useIntegrationProfile from '@/composables/useIntegrationProfile';
import useTrayConfig from '@/composables/useTrayConfig';
import { useFlashesStore, usePartnerCloudStore } from '@/stores';
import { TabValue } from '@/types/integrations';

import IntegrationSettingsContent from './IntegrationSettingsContent.vue';

const router = useRouter();

const { addErrorFlash } = useFlashesStore();
const partnerCloudStore = usePartnerCloudStore();

const isSavingAll = ref(false);

const {
  isTrayConfigUpdateRequired,
  enabledTray,
  integrationProfileType,
  saveTrayConfig,
  savePartnerFieldMappings,
  state,
} = useTrayConfig();

const {
  clearProfilesState,
  hasPermissionToManageIntegrations,
  saveIntegrationProfile,
} = useIntegrationProfile(integrationProfileType.value);

const confirmationModalType = ref<
  'exit-progress' | 'exit-changes' | 'delete' | 'discard-changes' | null
>(null);

type Tab = {
  name: string;
  value: TabValue;
  icon: [BittsIconPrefix, string];
  disabled?: boolean;
};
const tabs = computed<Tab[]>(() => [
  {
    name: 'General Settings',
    value: 'general-settings',
    icon: ['fak', 'settings'],
    disabled: state.isSaving,
  },
  {
    name: 'Crossbeam Custom Object',
    value: 'custom-object',
    icon: ['fak', 'wrench'],
    disabled: state.isSaving,
  },
  {
    name: `${
      integrationProfileType.value === 'hubspot' ? 'Company' : 'Account'
    } Object`,
    value: 'account-object',
    icon: ['fak', 'integration'],
    disabled: state.isSaving,
  },
]);

const currentTab = ref<TabValue>('general-settings');
const pendingTab = ref<TabValue | null>(null);

const hasChanges = computed(
  () =>
    state.hasTrayConfigChanges ||
    state.isPartnerFieldMappingsDirty ||
    state.isProfileSettingDirty,
);

function onChangeTab(tab: TabValue) {
  if (tab === currentTab.value) return;

  if (hasChanges.value) {
    pendingTab.value = tab;
    confirmationModalType.value = 'discard-changes';
    return;
  }
  currentTab.value = tab;
}

const avatarLogo = computed(() => {
  return enabledTray?.value?.avatar
    ? {
        logo_url: new URL(
          `../../assets/pngs/partner-cloud/${enabledTray?.value?.avatar}`,
          import.meta.url,
        ).href,
      }
    : null;
});

const isHubspot = computed(() => integrationProfileType.value === 'hubspot');

const isSalesforce = computed(
  () => integrationProfileType.value === 'salesforce',
);

const onDrawerClosed = () => {
  if (
    !enabledTray?.value?.enabled &&
    partnerCloudStore.inProgressInstallation
  ) {
    confirmationModalType.value = 'exit-progress';
  } else if (hasChanges.value) {
    confirmationModalType.value = 'exit-changes';
  } else {
    closeDrawerHandler();
  }
};

const closeDrawerHandler = () => {
  clearProfilesState();
  confirmationModalType.value = null;
  router.push({ name: 'integrations', query: { scroll: 'true' } });
};

async function triggerSaveTrayConfig() {
  if (isTrayConfigUpdateRequired.value) {
    await saveTrayConfig();
  }
}

const saveChanges = async () => {
  if (!integrationProfileType.value) {
    return onDrawerClosed();
  }

  isSavingAll.value = true;
  const promises = [triggerSaveTrayConfig()];

  if (currentTab.value === 'general-settings') {
    promises.push(saveIntegrationProfile());
  }

  if (currentTab.value === 'account-object' && state.partnerFieldMappingsPush) {
    promises.push(savePartnerFieldMappings());
  }

  await Promise.all(promises);
  isSavingAll.value = false;
  onDrawerClosed();
};

async function deleteIntegration() {
  if (!enabledTray.value?.id) return;
  try {
    state.isDeleting = true;
    await partnerCloudStore.deleteIntegration(enabledTray.value);
  } catch (error) {
    addErrorFlash({
      message: 'Failed to delete integration',
    });
  } finally {
    state.isDeleting = false;
    closeDrawerHandler();
  }
}

function onConfirmedTabChange() {
  state.resetState();
  confirmationModalType.value = null;
  currentTab.value = pendingTab.value ?? 'general-settings';
  pendingTab.value = null;
}

const modalProps = computed(() => {
  const base = {
    variant: 'confirm' as const,
    primaryButtonType: 'danger' as const,
    actionLoading: state.isDeleting,
    onClosed: () => {
      confirmationModalType.value = null;
    },
  };
  const exit = {
    ...base,
    title: 'Exit without saving',
    primaryButtonText: 'Exit',
    onAction: closeDrawerHandler,
  };

  switch (confirmationModalType.value) {
    case 'exit-changes':
      return {
        ...exit,
        description: `You've made changes that haven't been saved. Are you sure you want to exit without saving?`,
      };
    case 'exit-progress':
      return {
        ...exit,
        description: `You won't be able to push your Crossbeam overlaps into your custom object until you finish the setup`,
      };
    case 'delete':
      return {
        ...base,
        title: 'Delete integration',
        primaryButtonText: 'Delete Integration',
        description: 'Are you sure you want to delete this integration?',
        onAction: deleteIntegration,
      };
    case 'discard-changes':
      return {
        ...base,
        title: 'Discard Changes',
        primaryButtonText: 'Discard changes',
        description: `You've made changes that haven't been saved. Are you sure you want to continue without saving?`,
        onAction: onConfirmedTabChange,
      };
    default:
      return base;
  }
});

defineExpose({
  isTrayConfigUpdateRequired,
  state,
});
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";
.subtitle-section {
  @apply p-16 border-b border-neutral-background-disabled;
}
.subtitle-section-content {
  @apply flex items-center;
}

.c-integration-settings-alert :deep(div.self-start.pt-4) {
  @apply self-center! pt-0!;
}
</style>
